var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box, Flex, Text, Button, Spinner, Image } from 'pcln-design-system';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { DialogInputSection } from './DialogInputSection';
import { submitChatMessages } from './submitChatMessages';
import { ScrollIntoView } from './ScrollIntoView';
import { History } from 'pcln-icons';
import { DialogLoading } from './DialogLoading';
import { ChatbotDataContext } from './context/ChatbotDataContext';
import { submitMessageToLivePerson } from './LivePersonService/submitLivePersonMessage';
import { livePersonMessagesStatusManager } from './LivePersonService/livePersonMessageStatusManager';
import { EVENT_TYPE, MESSAGE_TYPE } from './LivePersonService/constantsLivePerson';
import analytics from './analytics';
import ErrorComponent from './components/ErrorComponent';
import UserInputBar from './Voice/UserInputBar';
import RecordingIndicator from './Voice/RecordingIndicator';
import pennyBroadcast, { BroadcastType, usePennySubscriber } from './utils/pennyBroadcast';
// import DebugComponent from './DebugComponent'
import { AnimatePresence } from 'framer-motion';
import { LoadMorePrompts, LoadMorePromptsWrapper } from './ChatDialogContent.styles';
import fetchWithTimeout from './utils/fetchWithTimeout';
import { ChatMessagesList } from './ChatMessagesCollections';
const MIC_SENSITIVITY = 1;
const MIC_TIMEOUT = 2000;
const GREETING_MESSAGE = "Hi, I'm Penny, your virtual travel assistant! How can I help you today?";
const TRAVEL_BOT_LOGO = 'https://s1.pclncdn.com/design-assets/pcln-bot/logos/pcln_bot_opener_logo_no_border_v1.1.svg';
/**
 * ChatDialogMainContentSection
 * Main chat content
 * @public
 */
export const ChatDialogMainContentSection = () => {
  var _a, _b;
  const {
    setCachedMessages,
    setCachedPrompts,
    setLoadMoreMessages,
    submitChatMessagesPayload,
    handleMarkerResponse,
    fetchRequestOptions,
    markerComponents,
    livePersonOptions = {},
    logFirstMessageSubmitted,
    errorMessage,
    setErrorMessage,
    isLoading,
    setLoadingStatus,
    chatMessages,
    setChatMessages,
    prompts,
    setPrompts,
    isDisablingInput,
    setIsDisablingInputValue,
    fullStreamingTexts,
    setStreamingText,
    livePersonConversationId,
    livePersonConnectionRequest,
    lpUserFirstName,
    setLPUserFirstName,
    lpUserLastName,
    setLPUserLastName,
    isLPUserFormSubmitted,
    setIsLPUserFormSubmitted,
    isLivePersonEnabled,
    setIsLivePersonEnabled,
    setChatSummary,
    debugMode,
    enableVoice,
    discoveryCitySelected,
    pushPrompt,
    setOpenErrorComponent,
    openErrorComponent,
    audioPlayerRef,
    transcriptionFetchRequestOptions,
    loadMoreMessages
  } = useContext(ChatbotDataContext);
  const contentRef = useRef(null);
  const isAudioPlaybackEnabledRef = useRef(false);
  const [mediaRecorder, setMediaRecorder] = useState();
  const [isRetrievingAudio, setIsRetrievingAudio] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [hasUserGrantedMicAccess, setHasUserGrantedMicAccess] = useState(false);
  const messagesEndRef = useRef(null);
  const loadRef = useRef(null);
  const [lastMessage, setLastmessage] = useState('');
  const [currScrollHeight, setCurrentScrollHeight] = useState(0);
  const {
    isSessionActive
  } = submitChatMessagesPayload;
  useEffect(() => {
    var _a, _b;
    if (chatMessages) {
      const lastContent = (_b = (_a = chatMessages[chatMessages.length - 1]) === null || _a === void 0 ? void 0 : _a.content) !== null && _b !== void 0 ? _b : 'lastContent';
      setLastmessage(lastContent);
    }
  }, [chatMessages]);
  useEffect(() => {
    var _a;
    (_a = messagesEndRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
      behavior: 'smooth'
    });
  }, [lastMessage]);
  // Ref necessary to hold current value of the state for use in useCallback. Otherwise we'd only use
  // the value of the state at initialization.
  const streamingAssistantMessageVoiceRef = useRef([]);
  const [streamingAssistantMessageVoice, setStreamingAssistantMessageVoice] = useState([]);
  const pennyBroadcastListener = useCallback(event => {
    if (event.data.type === BroadcastType.AUDIO_QUEUE_DRAINED) {
      setIsRetrievingAudio(false);
      setStreamingAssistantMessageVoice([]);
    } else if (event.data.type === BroadcastType.AUDIO_RETRIEVAL_INITIATED) {
      setIsRetrievingAudio(true);
    } else if (event.data.type === BroadcastType.AUDIO_PLAYBACK_STARTED) {
      setIsPlayingAudio(true);
      setStreamingAssistantMessageVoice([...streamingAssistantMessageVoiceRef.current, event.data.data]);
    } else if (event.data.type === BroadcastType.AUDIO_PLAYBACK_STOPPED) {
      setIsPlayingAudio(!!event.data.hasMoreMessages);
    } else if (event.data.type === BroadcastType.ENABLE_AUDIO_PLAYBACK) {
      isAudioPlaybackEnabledRef.current = true;
    } else if (event.data.type === BroadcastType.DISABLE_AUDIO_PLAYBACK) {
      isAudioPlaybackEnabledRef.current = false;
    }
  }, [setIsRetrievingAudio]);
  // This just ensures that the value in the ref is always the same as the state
  useEffect(() => {
    streamingAssistantMessageVoiceRef.current = [...streamingAssistantMessageVoice];
  }, [streamingAssistantMessageVoice]);
  /**
   * Checks to see if the user has previously granted access to their microphone.
   */
  useEffect(() => {
    const checkMicAccess = () => __awaiter(void 0, void 0, void 0, function* () {
      var _a;
      if (enableVoice) {
        if ((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.enumerateDevices) {
          try {
            const devices = yield navigator.mediaDevices.enumerateDevices();
            const hasUserPreviouslyGrantedMicAccess = devices.some(device => {
              return device.kind === 'audioinput' && !!device.label;
            });
            setHasUserGrantedMicAccess(hasUserPreviouslyGrantedMicAccess);
          } catch (err) {
            analytics.logError({
              message: 'Unable to detect microphone access',
              error: JSON.stringify(err)
            });
          }
        }
      }
    });
    checkMicAccess();
  }, [enableVoice]);
  usePennySubscriber(pennyBroadcastListener);
  const submitChatMessagesOptions = {
    setChatMessages,
    setPrompts,
    setStreamingText,
    setLoadingStatus,
    setErrorMessage,
    setIsDisablingInputValue,
    // Cache control
    cacheControl: {
      setCachedMessages,
      setCachedPrompts
    },
    requestOptions: fetchRequestOptions,
    markerComponents,
    handleMarkerResponse
  };
  const sendUserMessage = text => __awaiter(void 0, void 0, void 0, function* () {
    const chatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    const promptMessage = {
      role: 'user',
      content: text
    };
    setChatMessages([...chatMessages, chatMessage]);
    yield submitChatMessages(Object.assign({
      submitChatMessagesPayload: Object.assign(Object.assign({}, submitChatMessagesPayload), {
        prompts: [...prompts, promptMessage],
        includeSpeech: !!enableVoice && isAudioPlaybackEnabledRef.current
      }),
      // Local state control
      chatMessages: [...chatMessages, chatMessage]
    }, submitChatMessagesOptions));
  });
  useEffect(() => {
    if (discoveryCitySelected || pushPrompt) {
      const chatContent = discoveryCitySelected ? `Looking for hotels in ${discoveryCitySelected}` : pushPrompt;
      const chatMessage = {
        content: chatContent,
        date: new Date(),
        role: 'user'
      };
      const promptMessage = {
        content: chatContent,
        role: 'user'
      };
      const updatedChatMessages = [...chatMessages, chatMessage];
      //  set user's message
      setChatMessages(updatedChatMessages);
      setCachedMessages(chatMessage);
      setCachedPrompts(promptMessage);
      // submit user's message to get response
      submitChatMessages(Object.assign({
        submitChatMessagesPayload: Object.assign(Object.assign({}, submitChatMessagesPayload), {
          includeSpeech: !!enableVoice && isAudioPlaybackEnabledRef.current,
          prompts: isSessionActive ? [promptMessage] : [...prompts, promptMessage]
        }),
        setOpenErrorComponent,
        // Local state control
        chatMessages: updatedChatMessages
      }, submitChatMessagesOptions));
    }
  }, [discoveryCitySelected, pushPrompt]);
  const {
    submitMessageURL,
    handleLivePersonOnMessageError,
    chatCustomerId
  } = livePersonOptions;
  const sendAudioToTranscriptionService = file => __awaiter(void 0, void 0, void 0, function* () {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = yield fetchWithTimeout((transcriptionFetchRequestOptions === null || transcriptionFetchRequestOptions === void 0 ? void 0 : transcriptionFetchRequestOptions.url) || '/genai-svc/genai/transcriptions', {
        method: 'POST',
        body: formData,
        headers: {
          accept: 'application/json'
        }
      }, (transcriptionFetchRequestOptions === null || transcriptionFetchRequestOptions === void 0 ? void 0 : transcriptionFetchRequestOptions.timeout) || 10000);
      const data = yield response.json();
      return data.text;
    } catch (e) {
      analytics.logError({
        message: 'Error submitting audio to transcription service',
        error: JSON.stringify(e)
      });
    }
  });
  const startRecording = function () {
    return __awaiter(this, void 0, void 0, function* () {
      const audioPlayer = audioPlayerRef.current;
      audioPlayer.autoplay = true;
      // This is some dummy inperceptible audio content that can be played when teh user clicks the start recording
      // button so that the audio element has access to play audio in the future.
      audioPlayer.src = 'data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';
      // Check if the user has granted mic access. If they haven't, request it.
      if (!hasUserGrantedMicAccess) {
        yield navigator.mediaDevices.getUserMedia({
          audio: true
        });
        setHasUserGrantedMicAccess(true);
        return;
      }
      // Send message to stop playback
      pennyBroadcast({
        type: BroadcastType.INTERRUPT_AUDIO_PLAYBACK,
        sender: 'ChatDialogContent.startRecording()'
      });
      try {
        const stream = yield navigator.mediaDevices.getUserMedia({
          audio: true
        });
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        analyser.fftSize = 2048;
        const recorder = new MediaRecorder(stream);
        setMediaRecorder(recorder);
        const chunks = [];
        recorder.ondataavailable = event => {
          chunks.push(event.data);
        };
        let silenceStart = performance.now();
        let isRecording = true;
        let userSpoken = false;
        const checkForAudioInput = () => {
          if (!isRecording) {
            return;
          }
          const dataArray = new Uint8Array(analyser.fftSize);
          analyser.getByteTimeDomainData(dataArray);
          const currTime = performance.now();
          if (dataArray.some(value => value - 128 > MIC_SENSITIVITY || value - 128 < -MIC_SENSITIVITY)) {
            if (!userSpoken) {
              pennyBroadcast({
                type: BroadcastType.USER_IS_TALKING,
                sender: 'ChatDialogContent.checkSilence()'
              });
              userSpoken = true;
            }
            silenceStart = currTime; // Reset the silence start time
          } else if (userSpoken && currTime - silenceStart > MIC_TIMEOUT) {
            pennyBroadcast({
              type: BroadcastType.USER_IS_SILENT,
              sender: 'ChatDialogContent.checkSilence()'
            });
            recorder.stop();
            isRecording = false;
          }
          if (isRecording) {
            requestAnimationFrame(checkForAudioInput);
          }
        };
        checkForAudioInput();
        recorder.onstop = () => __awaiter(this, void 0, void 0, function* () {
          pennyBroadcast({
            type: BroadcastType.VOICE_RECORDING_STOPPED,
            sender: 'ChatDialogContent.onstop()'
          });
          // Combine the chunks into a single audio Blob
          const audioBlob = new Blob(chunks, {
            type: 'audio/mp3'
          });
          const audioFile = new File([audioBlob], 'audio_rec.mp3', {
            type: 'audio/mp3'
          });
          // take the audio blob and send it to the server
          try {
            const text = yield sendAudioToTranscriptionService(audioFile);
            yield sendUserMessage(text);
          } catch (e) {
            analytics.logError({
              message: 'Could not transcribe audio',
              error: JSON.stringify(e)
            });
          }
        });
        const isSafari = navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1;
        const timeSlice = isSafari ? 1000 : null;
        recorder.start(timeSlice);
        pennyBroadcast({
          type: BroadcastType.VOICE_RECORDING_STARTED,
          sender: 'ChatDialogContent'
        });
        pennyBroadcast({
          type: BroadcastType.ENABLE_AUDIO_PLAYBACK,
          sender: 'ChatDialogContent'
        });
      } catch (error) {
        analytics.logError({
          message: 'Could not start recording',
          error: JSON.stringify(error)
        });
      }
    });
  };
  const stopRecording = function () {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    }
  };
  useEffect(() => {
    const onScroll = () => {
      const scrollableDiv = contentRef.current;
      if (!scrollableDiv) {
        return;
      }
    };
    // eslint-disable-next-line no-unused-expressions
    contentRef.current && contentRef.current.addEventListener('scroll', onScroll);
    return () => contentRef.current && contentRef.current.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    var _a;
    const curHeight = (_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollHeight;
    contentRef.current.scrollTop = curHeight - currScrollHeight;
  }, [loadMoreMessages]);
  // Auto submit the first message to init the conversation with ChatBot
  useEffect(() => {
    if (chatMessages.length === 0 && !isLivePersonEnabled) {
      submitChatMessages(Object.assign({
        submitChatMessagesPayload: Object.assign(Object.assign({}, submitChatMessagesPayload), {
          prompts,
          includeSpeech: !!enableVoice && isAudioPlaybackEnabledRef.current
        }),
        setOpenErrorComponent,
        chatMessages
      }, submitChatMessagesOptions));
    }
  }, [chatMessages.length === 0]);
  function submitChatMessageInMarkerComponent(msg, updatedChatMessage) {
    submitChatMessages(Object.assign({
      submitChatMessagesPayload: Object.assign(Object.assign({}, submitChatMessagesPayload), {
        includeSpeech: !!enableVoice && isAudioPlaybackEnabledRef.current,
        prompts: isSessionActive ? [msg] : [...prompts, msg]
      }),
      setOpenErrorComponent,
      // Local state control
      chatMessages: !Array.isArray(updatedChatMessage) || updatedChatMessage.length === 0 ? chatMessages : updatedChatMessage
    }, submitChatMessagesOptions));
  }
  if (openErrorComponent) {
    return React.createElement(ErrorComponent, null);
  }
  return React.createElement(AnimatePresence, null, React.createElement(Box, {
    width: 1,
    key: 'chat-dialog-transcript-container',
    height: enableVoice ? 'calc(100% - 133px)' : 'calc(100% - 120px)',
    style: {
      position: 'absolute',
      top: '65px',
      overflowY: 'scroll',
      overflowX: 'hidden',
      borderBottom: enableVoice ? `1px solid #c0cad5` : ''
    },
    ref: contentRef
  }, React.createElement(Flex, {
    p: 3,
    width: 1,
    minHeight: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-end'
  }, ((_a = chatMessages === null || chatMessages === void 0 ? void 0 : chatMessages[0]) === null || _a === void 0 ? void 0 : _a.content) !== GREETING_MESSAGE && ((_b = chatMessages === null || chatMessages === void 0 ? void 0 : chatMessages[0]) === null || _b === void 0 ? void 0 : _b.displayComponentName) !== 'SESSION_INITIALIZED' && isSessionActive && !loadMoreMessages ? React.createElement(LoadMorePromptsWrapper, {
    ref: loadRef
  }, React.createElement(LoadMorePrompts, {
    onClick: function () {
      var _a;
      setLoadMoreMessages(true);
      setCurrentScrollHeight((_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollHeight);
    }
  }, React.createElement(Button, {
    variation: 'link'
  }, ' ', React.createElement(History, {
    size: 20,
    mr: 2
  }), " View Chat History"), React.createElement(Text, {
    fontSize: 0,
    color: 'text.lightest'
  }, "View More History"))) : null, React.createElement(Flex, {
    justifyContent: 'center'
  }, loadMoreMessages ? React.createElement(Box, {
    mb: 4
  }, React.createElement(Spinner, {
    size: 40,
    color: 'purple'
  }, React.createElement(Image, {
    src: TRAVEL_BOT_LOGO,
    width: 28,
    alt: 'Penny chat avatar'
  }))) : null), React.createElement(ChatMessagesList, {
    chatMessages: chatMessages,
    prompts: prompts,
    isRetrievingAudio: isRetrievingAudio,
    markerComponents: markerComponents,
    isLoading: isLoading,
    debugMode: debugMode,
    setIsDisablingInputValue: setIsDisablingInputValue,
    setChatMessages: setChatMessages,
    submitMessages: submitChatMessageInMarkerComponent,
    livePersonConnectionRequest: livePersonConnectionRequest,
    setLPUserFirstName: setLPUserFirstName,
    setLPUserLastName: setLPUserLastName,
    lpUserFirstName: lpUserFirstName,
    lpUserLastName: lpUserLastName,
    isLPUserFormSubmitted: isLPUserFormSubmitted,
    setIsLPUserFormSubmitted: setIsLPUserFormSubmitted,
    isLivePersonEnabled: isLivePersonEnabled,
    setIsLivePersonEnabled: setIsLivePersonEnabled,
    submitChatSummary: setChatSummary,
    submitChatMessageInMarkerComponent: submitChatMessageInMarkerComponent,
    submitQuickActionMessage: function (action) {
      const {
        chatContent,
        promptContent
      } = action;
      const promptMessage = {
        content: promptContent || chatContent,
        role: 'user'
      };
      const chatMessage = {
        content: chatContent,
        role: 'user'
      };
      const updatedChatMessages = [...chatMessages, chatMessage];
      // set user's message
      setChatMessages(updatedChatMessages);
      setCachedMessages(chatMessage);
      setCachedPrompts(promptMessage);
      // submit user's message to get response
      submitChatMessages(Object.assign({
        submitChatMessagesPayload: Object.assign(Object.assign({}, submitChatMessagesPayload), {
          prompts: isSessionActive ? [promptMessage] : [...prompts, promptMessage],
          includeSpeech: !!enableVoice && isAudioPlaybackEnabledRef.current
        }),
        setOpenErrorComponent,
        // Local state control
        chatMessages: updatedChatMessages
      }, submitChatMessagesOptions));
    }
  }), errorMessage && React.createElement(ScrollIntoView, null, React.createElement(Box, {
    my: 3,
    p: 3
  }, React.createElement(Text, {
    color: 'error.base'
  }, errorMessage), React.createElement(Text, {
    color: 'error.base'
  }, "Please retry!"))), !isAudioPlaybackEnabledRef.current && isLoading &&
  // loading status for the bot only
  React.createElement(ScrollIntoView, null, React.createElement(DialogLoading, {
    streamingText: fullStreamingTexts
  })), isAudioPlaybackEnabledRef.current && (isRetrievingAudio || isLoading) && React.createElement(ScrollIntoView, null, React.createElement(DialogLoading, {
    enableVoice: true,
    playingAudio: isPlayingAudio,
    streamingText: streamingAssistantMessageVoice.join(' ')
  })),
  // There is no loading status for live person, we will display whatever status text we got
  isLivePersonEnabled && fullStreamingTexts && React.createElement(ScrollIntoView, null, React.createElement(Text, {
    fontStyle: 'italic',
    color: 'text.light',
    px: 3
  }, fullStreamingTexts))), React.createElement("div", {
    ref: messagesEndRef,
    style: {
      height: 0
    }
  })), React.createElement(Box, {
    width: 1,
    style: {
      position: 'absolute',
      bottom: '0px'
    },
    key: 'chat-dialog-user-input-container'
  }, enableVoice ? React.createElement(React.Fragment, null, React.createElement(UserInputBar, {
    onTextSubmit: function (msg) {
      pennyBroadcast({
        type: BroadcastType.INTERRUPT_AUDIO_PLAYBACK,
        sender: 'ChatDialogContent.startRecording()'
      });
      // Set the error message as empty if there is any
      setErrorMessage('');
      // cache the msg on local for reuse
      const chatMessage = {
        role: 'user',
        content: msg,
        date: new Date()
      };
      const promptMessage = {
        role: 'user',
        content: msg
      };
      setCachedMessages(chatMessage);
      setCachedPrompts(promptMessage);
      const updatedChatMessages = [...chatMessages, chatMessage];
      // Update the message showing in the dialog
      setChatMessages([...chatMessages, chatMessage]);
      pennyBroadcast({
        type: BroadcastType.USER_SUBMITTED_TEXT,
        data: msg,
        sender: 'ChatDialogContent.onTextSubmit()'
      });
      // Submit the user message
      submitChatMessages(Object.assign({
        submitChatMessagesPayload: Object.assign(Object.assign({}, submitChatMessagesPayload), {
          prompts: [...prompts, promptMessage],
          includeSpeech: !!enableVoice && isAudioPlaybackEnabledRef.current
        }),
        setOpenErrorComponent,
        // Local state control
        chatMessages: updatedChatMessages
      }, submitChatMessagesOptions));
      // Log first user submitted message to chatbot
      if (chatMessages.length <= 1 && logFirstMessageSubmitted && typeof logFirstMessageSubmitted === 'function') {
        logFirstMessageSubmitted(chatMessage.content);
      }
    },
    onRecord: startRecording,
    onStopRecording: stopRecording
  }), React.createElement(RecordingIndicator, {
    onClose: stopRecording
  })) : React.createElement(DialogInputSection, {
    isLivePersonEnabled: isLivePersonEnabled,
    // from local state
    livePersonConversationId: livePersonConversationId,
    isDisablingInput: isDisablingInput,
    isLoading: isLoading,
    submitInputValue: function (msg) {
      // Set the error message as empty if there is any
      setErrorMessage('');
      // cache the msg on local for reuse
      const msgWithDate = Object.assign(Object.assign({}, msg), {
        date: new Date()
      });
      setCachedMessages(msgWithDate);
      setCachedPrompts(msg);
      const updatedChatMessages = [...chatMessages, msgWithDate];
      // Update the message showing in the dialog
      setChatMessages(updatedChatMessages);
      // Submit the user message
      submitChatMessages(Object.assign({
        submitChatMessagesPayload: Object.assign(Object.assign({}, submitChatMessagesPayload), {
          prompts: isSessionActive ? [msg] : [...prompts, msg],
          includeSpeech: !!enableVoice && isAudioPlaybackEnabledRef.current
        }),
        setOpenErrorComponent,
        // Local state control
        chatMessages: updatedChatMessages
      }, submitChatMessagesOptions));
      // Log first user submitted message to chatbot
      if (chatMessages.length <= 1 && logFirstMessageSubmitted && typeof logFirstMessageSubmitted === 'function') {
        logFirstMessageSubmitted(msg.content);
      }
    },
    submitMessageToLivePerson: function (userMessage) {
      // Set the error message as empty if there is any
      setErrorMessage('');
      // disabled the input field until current message submitted
      setIsDisablingInputValue(true);
      // Create a user message to LP service
      const lpData = {
        messageType: MESSAGE_TYPE.MESSAGE,
        conversationId: livePersonConversationId,
        eventType: EVENT_TYPE.ContentEvent,
        eventDesc: userMessage.content
      };
      const msgId = uuidV4();
      const livePersonMessagePayload = {
        user: {
          firstName: lpUserFirstName,
          lastName: lpUserLastName
        },
        lpData,
        chatCustomerId,
        msgId
      };
      livePersonMessagesStatusManager.updateMessageStatusByMsgId(msgId, '');
      const updatedChatMessages = [...chatMessages, Object.assign(Object.assign({}, userMessage), {
        msgId
      })];
      // Update the user message showing in the dialog
      setChatMessages(updatedChatMessages);
      setCachedMessages(undefined, updatedChatMessages);
      submitMessageToLivePerson({
        requestOptions: {
          url: submitMessageURL,
          headers: {}
        },
        livePersonMessagePayload,
        handleLivePersonOnMessageError
      }).then(({
        errorMessage
      }) => {
        setIsDisablingInputValue(false);
        if (errorMessage) {
          setErrorMessage(errorMessage);
        } else {
          livePersonMessagesStatusManager.updateMessageStatusByMsgId(msgId, 'SENT');
        }
      });
    },
    submitUserChatStateEvent: function (status) {
      const lpData = {
        messageType: MESSAGE_TYPE.MESSAGE,
        conversationId: livePersonConversationId,
        eventType: EVENT_TYPE.ChatStateEvent,
        eventDesc: status
      };
      const msgId = uuidV4();
      const livePersonMessagePayload = {
        lpData,
        user: {
          firstName: lpUserFirstName,
          lastName: lpUserLastName
        },
        chatCustomerId,
        msgId
      };
      livePersonMessagesStatusManager.updatePendingMessageStatus(() => __awaiter(this, void 0, void 0, function* () {
        yield submitMessageToLivePerson({
          requestOptions: {
            url: submitMessageURL,
            headers: {}
          },
          livePersonMessagePayload,
          handleLivePersonOnMessageError
        });
      }));
    },
    submitUserAcceptStatusEvent: function (status) {
      const sequenceList = livePersonMessagesStatusManager.getPendingAgentSequenceIds();
      if (sequenceList.length === 0) {
        return;
      }
      const lpData = {
        messageType: MESSAGE_TYPE.MESSAGE,
        conversationId: livePersonConversationId,
        eventType: EVENT_TYPE.AcceptStatusEvent,
        eventDesc: status,
        sequenceList: livePersonMessagesStatusManager.getPendingAgentSequenceIds()
      };
      const msgId = uuidV4();
      const livePersonMessagePayload = {
        lpData,
        user: {
          firstName: lpUserFirstName,
          lastName: lpUserLastName
        },
        chatCustomerId,
        msgId
      };
      livePersonMessagesStatusManager.updatePendingMessageStatus(() => __awaiter(this, void 0, void 0, function* () {
        yield submitMessageToLivePerson({
          requestOptions: {
            url: submitMessageURL,
            headers: {}
          },
          livePersonMessagePayload,
          handleLivePersonOnMessageError
        });
        livePersonMessagesStatusManager.clearPendingAgentSequenceIds();
      }));
    }
  })));
};